import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';

import { getGaAttributeObject } from '~shared/utils/google-analytics';
import { Link } from '../../../spa/Components/Link/Link';
import { highlightText } from '../../utils/string';
import './_widgetContainer.scss';

const baseClass = 'c-widgetcontainer';

const propTypes = {
    title: PropTypes.node,
    titleIconClassName: PropTypes.string,
    highlight: PropTypes.string,
    boldText: PropTypes.bool,
    imgSource: PropTypes.string,
    children: PropTypes.oneOfType([ PropTypes.object, PropTypes.array ]).isRequired,
    titleLink: PropTypes.string,
    subtitle: PropTypes.node,
    sportSlug: PropTypes.string,
    gaComponent: PropTypes.string,
    gtmAttribute: PropTypes.string,
    onClick: PropTypes.func,
    previousArrow: PropTypes.object,
    nextArrow: PropTypes.object,
    noMarginTop: PropTypes.bool,
    isFavouriteSport: PropTypes.bool,
};

/**
 * Container for common widgets
 *
 * @param {String} title Title text
 * @param {String} titleIconClassName Icon class name
 * @param {String} highlight Part of the string to be highlighted
 * @param {Boolean} boldText Set true for font-weight bold
 * @param {String} imgSource Image src string
 * @param {*} children List of React components
 * @param {*} subtitle Subtitle
 * @param {String} sportSlug sport slug for GTM tags
 * @param {string} gaComponent GA component name for betslip tracking
 * @param {string} gtmAttribute GTM tag for components
 * @returns {*} React element
 */
function WidgetContainer({
        title,
        titleIconClassName,
        highlight,
        boldText,
        imgSource,
        children,
        titleLink,
        subtitle,
        gaComponent = null,
        gtmAttribute,
        onClick,
        previousArrow,
        nextArrow,
        noMarginTop = false,
        isFavouriteSport = false,
    }) {
    const [navArrowsEl, setNavArrowsEl] = useState(null);

    const titleTextClass = boldText ? `${baseClass}__text ${baseClass}__text--bold` : `${baseClass}__text`;
    const titleIconClass = typeof titleIconClassName !== 'undefined' ? `${baseClass}__icon ${baseClass}__icon-${titleIconClassName}` : '';
    const seeAllTitleLinkClass = `${baseClass}__see-all-title--link`;
    const seeAllClass = `${baseClass}__see-all`;
    const seeAllClassNav = `${seeAllClass}--nav`;
    const navigationClass = `${baseClass}__navigation`;
    const navigationArrowsClass = `${navigationClass}--arrows`;
    const removeMarginClass = noMarginTop ? `${baseClass}__title--nomargin-top` : '';

    const I18N_PREFIX = 'betcard';
    const SEE_ALL_TEXT = `${I18N_PREFIX}.see_all`;

    let widgetWrapperAttributes = { className: baseClass };
    if (gaComponent) {
        widgetWrapperAttributes = {
            ...widgetWrapperAttributes,
            ...getGaAttributeObject(gaComponent),
        };
    }

    const favSportsStar = (
        <div className='fav-sports-star'/>
    );

    const NavigationArrows = ( {children} ) => (
        <div className={`${navigationClass}--arrows-container`}>{children}</div>
     );

     const NavigationLink = () => (
        <Link className={`${seeAllTitleLinkClass} ${navigationArrowsClass}`} to={titleLink} onClick={onClick} gtmAttribute={`${gtmAttribute}`}> </Link>
     );

     const navArrows = (
        <span className={`${seeAllClass} ${seeAllClassNav}`}>
            <NavigationArrows>{previousArrow}{nextArrow}</NavigationArrows>
            <Trans>{SEE_ALL_TEXT}</Trans>
        </span>
     );

     const seeAllText = (<span className={seeAllClass}><Trans>{SEE_ALL_TEXT}</Trans></span>);

    useEffect(() => {
        setNavArrowsEl(navArrows);
    }, []);

    return (
        <div { ...widgetWrapperAttributes }>
            { // TITLE with see all link
                (Boolean(title) || Boolean(highlight) || Boolean(imgSource)) &&
                    <div className={`${baseClass}__title ${titleIconClass} ${removeMarginClass}`}>
                        { // header without arrows
                            Boolean(titleLink) && Boolean(!previousArrow) && Boolean(!nextArrow) &&
                            <Link className={`${seeAllTitleLinkClass}`} to={titleLink} onClick={onClick} gtmAttribute={`${gtmAttribute}`}>
                        { // default text title
                            (Boolean(title) && !highlight && !imgSource) &&
                                <h2 className={titleTextClass}>
                                    <span className={`${titleTextClass}__heading c-widgetcontainer__text__title`}>
                                        {title}
                                        {isFavouriteSport && favSportsStar}
                                    </span>
                                    {seeAllText}
                                </h2>
                        }
                        { // highlighted text title
                            (Boolean(title) && Boolean(highlight) && !imgSource) &&
                                <h2 className={titleTextClass}
                                    dangerouslySetInnerHTML={{__html:highlightText(title, highlight, baseClass)}}>
                                    {seeAllText}
                                </h2>
                        }
                        { // image title
                            (Boolean(imgSource) && !title && !highlight) &&
                            <h2 className={titleTextClass}>
                                <img className={`${baseClass}__img`} src={imgSource} />
                                {seeAllText}
                            </h2>
                        }
                        </Link>
                        }

                        { // header with slider arrows
                            Boolean(titleLink) && Boolean(previousArrow) && Boolean(nextArrow) && <>
                        { // default text title
                            (Boolean(title) && !highlight && !imgSource) && <>
                                <h2 className={`${titleTextClass} ${navigationClass}`}>
                                    <span className='c-widgetcontainer__text__title'>
                                        {title}
                                        {isFavouriteSport && favSportsStar}
                                    </span>
                                    {navArrowsEl}
                                </h2>
                                <NavigationLink />
                            </>
                        }
                        { // highlighted text title
                            (Boolean(title) && Boolean(highlight) && !imgSource) && <>
                                <h2 className={`${titleTextClass} ${navigationClass}`}
                                    dangerouslySetInnerHTML={{__html:highlightText(title, highlight, baseClass)}}>
                                    {navArrowsEl}
                                </h2>
                                <NavigationLink />
                            </>
                        }
                        { // image title
                            (Boolean(imgSource) && !title && !highlight) && <>
                            <h2 className={`${titleTextClass} ${navigationClass}`}>
                                <img className={`${baseClass}__img`} src={imgSource} />
                                {navArrowsEl}
                                </h2>
                                <NavigationLink />
                            </>
                        }
                        </>
                        }

                        { // TITLE without see all link
                            Boolean(!titleLink) && <>
                            { // default text title
                                (Boolean(title) && !highlight && !imgSource) &&
                                    <h2 className={`${titleTextClass} ${navigationClass}`}>
                                        {title}
                                        {
                                            previousArrow && nextArrow && (
                                                <span className={seeAllClassNav}>
                                                    <NavigationArrows>{previousArrow}{nextArrow}</NavigationArrows>
                                                </span>
                                            )
                                        }
                                    </h2>
                            }
                            { // highlighted text title
                                (Boolean(title) && Boolean(highlight) && !imgSource) &&
                                    <h2 className={titleTextClass}
                                        dangerouslySetInnerHTML={{__html:highlightText(title, highlight, baseClass)}}>
                                    </h2>
                            }
                            { // image title
                                (Boolean(imgSource) && !title && !highlight) &&
                                <h2 className={titleTextClass}>
                                    <img className={`${baseClass}__img`} src={imgSource} />
                                </h2>
                            }
                            </>
                        }
                    </div>
            }
            { // SUBTITLE
                Boolean(subtitle) &&
                <div className={`${baseClass}__subtitle`}>
                    <span className={`${baseClass}__info-icon`}></span>
                    <span>{subtitle}</span>
                </div>
            }

            <div className={`${baseClass}__container`}>{children}</div>
        </div>
    );
}

WidgetContainer.propTypes = propTypes;

export default WidgetContainer;
